import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { DocumentQaSummary, QaSummaryHelper } from 'src/app/core/service/qa-summary-helper';
import {
  ResultCompareComparisonEntry,
  ResultCompareConsistency,
  ResultCompareEnrichedResponse,
} from 'src/app/core/service/result-compare.service';
import { ToastService } from 'src/app/toast/toast.service';
import { DocumentDataFileInfo } from '../shell.model';

@Component({
  selector: 'app-document-qa-info',
  templateUrl: './document-qa-info.component.html',
  styleUrls: ['./document-qa-info.component.scss'],
  standalone: true,
  imports: [CommonModule, NgbTooltipModule],
})
export class DocumentQaInfoComponent implements OnChanges {
  @Input() tableId = '';
  @Input() fileId = '';
  @Input() jobId = '';
  @Input() active = false;
  @Input() disabled = false;
  @Input() documentDataFileInfo: DocumentDataFileInfo | null = null;
  /** When true, uses batch comparison for Dataset tab (compare two dataspaces by batch ID). */
  @Input() isDatasetView = false;
  /** When true, uses schema (table versions) comparison for Data Fields tab. */
  @Input() isSchemaView = false;

  status: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  errorMessage = '';
  summary: DocumentQaSummary | null = null;
  /** Dataset (batch comparison) state for isDatasetView. */
  datasetStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  datasetErrorMessage = '';
  datasetConsistency: ResultCompareConsistency | null = null;
  datasetComparisons: ResultCompareComparisonEntry[] = [];
  /** Schema (table-versions) state for Data Fields tab. */
  schemaStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  schemaErrorMessage = '';
  schemaResponse: ResultCompareEnrichedResponse | null = null;
  private requestId = 0;
  private pendingPromise: Promise<void> | null = null;
  private datasetRequestId = 0;
  private pendingDatasetPromise: Promise<void> | null = null;
  private schemaRequestId = 0;
  private pendingSchemaPromise: Promise<void> | null = null;

  constructor(
    public qaSurfacesService: QaSummaryHelper,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isDatasetView) {
      if (changes['active'] || changes['tableId'] || changes['isDatasetView']) {
        if (this.active && this.tableId) {
          this.prefetchDataset();
        } else {
          this.resetDatasetState();
        }
      }
    } else if (this.isSchemaView) {
      if (changes['active'] || changes['tableId'] || changes['isSchemaView']) {
        if (this.active && this.tableId) {
          if (changes['tableId']) this.resetSchemaState();
          this.prefetchSchema();
        } else {
          this.resetSchemaState();
        }
      }
    } else {
      if (changes['active'] || changes['jobId'] || changes['fileId']) {
        if (this.active && this.jobId && this.fileId && this.tableId) {
          this.prefetch();
        } else {
          this.resetState();
        }
      }
    }
  }

  prefetchSchema(): void {
    void this.ensureSchemaComparison(false);
  }

  prefetch(): void {
    void this.ensureSummary(false);
  }

  async onQaButtonClick(): Promise<void> {
    if (this.isSchemaView && this.tableId) {
      await this.copySchemaToClipboard();
      return;
    }
    if (this.isDatasetView && this.tableId) {
      await this.copyDatasetToClipboard();
      return;
    }
    await this.copyToClipboard();
  }

  prefetchDataset(): void {
    void this.ensureDatasetComparison(false);
  }

  private async ensureDatasetComparison(force: boolean): Promise<void> {
    if (!this.tableId) return;
    if (!force && this.datasetStatus === 'ready' && this.datasetConsistency != null) return;
    if (!force && this.datasetStatus === 'loading' && this.pendingDatasetPromise) {
      await this.pendingDatasetPromise;
      return;
    }

    this.datasetStatus = 'loading';
    this.datasetErrorMessage = '';
    this.datasetConsistency = null;
    const requestId = ++this.datasetRequestId;

    const pending = (async () => {
      try {
        const response = await this.qaSurfacesService.getDatasetBatchComparison(this.tableId);
        if (requestId !== this.datasetRequestId) return;
        this.datasetConsistency = response?.comparison?.consistency ?? null;
        this.datasetComparisons = response?.comparison?.comparisons ?? [];
        this.datasetStatus = this.datasetConsistency != null ? 'ready' : 'error';
        if (this.datasetStatus === 'error' && !this.datasetConsistency) {
          this.datasetErrorMessage = 'Fewer than 2 batches to compare.';
        }
      } catch (error) {
        if (requestId !== this.datasetRequestId) return;
        this.datasetStatus = 'error';
        this.datasetErrorMessage = error instanceof Error ? error.message : 'Dataset comparison failed.';
      } finally {
        if (this.datasetRequestId === requestId) {
          this.pendingDatasetPromise = null;
        }
      }
    })();

    this.pendingDatasetPromise = pending;
    await pending;
  }

  private resetDatasetState(): void {
    this.datasetStatus = 'idle';
    this.datasetErrorMessage = '';
    this.datasetConsistency = null;
    this.datasetComparisons = [];
    this.pendingDatasetPromise = null;
  }

  private buildDatasetClipboardText(): string {
    const lines: string[] = [];
    lines.push('QA Summary (Dataspace)');
    lines.push('Compared vs previous batch');
    lines.push('');

    const c = this.datasetConsistency;
    if (c) {
      lines.push('Consistency');
      lines.push(`Overall: ${c.overall_percentage ?? '—'}%`);
      lines.push(`Level: ${c.level ?? '—'}`);
      lines.push(`Quality: ${c.quality ?? '—'}`);
      lines.push(`Total paths: ${c.total_paths ?? '—'}`);
      if (c.penalty_breakdown) {
        const pb = c.penalty_breakdown;
        lines.push(`Penalty breakdown: all_match ${pb.all_match ?? '—'}, value_different ${pb.value_different ?? '—'}, missing_key ${pb.missing_key ?? '—'}`);
      }
      lines.push('');
    }

    lines.push('Comparisons');
    lines.push(this.datasetComparisons?.length ? JSON.stringify(this.datasetComparisons, null, 2) : '—');
    return lines.join('\n');
  }

  private async copyDatasetToClipboard(): Promise<void> {
    if (this.datasetStatus !== 'ready' || this.datasetConsistency == null) {
      this.toastService.showError(
        'Unavailable',
        this.datasetErrorMessage || 'Dataset QA summary is unavailable.'
      );
      return;
    }

    const text = this.buildDatasetClipboardText();
    try {
      await navigator.clipboard.writeText(text);
      this.toastService.showInfo(
        'Copied!',
        'Dataspace QA summary copied to clipboard'
      );
    } catch {
      this.toastService.showError('Error', 'Failed to copy to clipboard');
    }
  }

  private async ensureSchemaComparison(force: boolean): Promise<void> {
    if (!this.tableId) return;
    if (!force && this.schemaStatus === 'ready' && this.schemaResponse != null) return;
    if (!force && this.schemaStatus === 'loading' && this.pendingSchemaPromise) {
      await this.pendingSchemaPromise;
      return;
    }

    this.schemaStatus = 'loading';
    this.schemaErrorMessage = '';
    this.schemaResponse = null;
    const requestId = ++this.schemaRequestId;

    const pending = (async () => {
      try {
        const response = await this.qaSurfacesService.getSchemaTableVersionsComparison(this.tableId);
        if (requestId !== this.schemaRequestId) return;
        this.schemaResponse = response;
        this.schemaStatus = response != null ? 'ready' : 'error';
        if (this.schemaStatus === 'error') {
          this.schemaErrorMessage = 'Schema comparison unavailable.';
        }
      } catch (error) {
        if (requestId !== this.schemaRequestId) return;
        this.schemaStatus = 'error';
        this.schemaErrorMessage = error instanceof Error ? error.message : 'Schema comparison failed.';
      } finally {
        if (this.schemaRequestId === requestId) {
          this.pendingSchemaPromise = null;
        }
      }
    })();

    this.pendingSchemaPromise = pending;
    await pending;
  }

  private resetSchemaState(): void {
    this.schemaStatus = 'idle';
    this.schemaErrorMessage = '';
    this.schemaResponse = null;
    this.pendingSchemaPromise = null;
  }

  private async copySchemaToClipboard(): Promise<void> {
    await this.ensureSchemaComparison(false);
    if (this.schemaStatus !== 'ready' || this.schemaResponse == null) {
      this.toastService.showError(
        'Unavailable',
        this.schemaErrorMessage || 'Schema QA (table-versions) is unavailable.'
      );
      return;
    }

    const text = JSON.stringify(this.schemaResponse, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      this.toastService.showInfo(
        'Copied!',
        'Schema QA (table-versions) copied to clipboard as JSON'
      );
    } catch {
      this.toastService.showError('Error', 'Failed to copy to clipboard');
    }
  }

  async copyToClipboard(): Promise<void> {
    await this.ensureSummary(false);
    if (this.status !== 'ready' || !this.summary) {
      this.toastService.showError(
        'Unavailable',
        this.errorMessage || 'QA summary is unavailable right now.'
      );
      return;
    }

    const clipboardText = this.qaSurfacesService.buildDocumentClipboardText(
      this.summary,
      this.documentDataFileInfo
    );
    navigator.clipboard
      .writeText(clipboardText)
      .then(() => {
        this.toastService.showInfo(
          'Copied!',
          'Document QA summary copied to clipboard'
        );
      })
      .catch(() => {
        this.toastService.showError('Error', 'Failed to copy QA summary');
      });
  }

  private async ensureSummary(force: boolean): Promise<void> {
    if (!this.jobId || !this.fileId || !this.tableId) {
      this.status = 'error';
      this.errorMessage = 'QA summary is unavailable for this document.';
      return;
    }

    if (!force && this.status === 'ready' && this.summary?.jobId === this.jobId) {
      return;
    }

    if (!force && this.status === 'loading' && this.pendingPromise) {
      await this.pendingPromise;
      return;
    }

    this.status = 'loading';
    this.errorMessage = '';
    const requestId = ++this.requestId;

    const pending = (async () => {
      try {
        const summary = await this.qaSurfacesService.getDocumentQaSummary({
          tableId: this.tableId,
          fileId: this.fileId,
          currentJobId: this.jobId,
        });

        if (requestId !== this.requestId) {
          return;
        }

        this.summary = summary;
        this.status = 'ready';
      } catch (error) {
        if (requestId !== this.requestId) {
          return;
        }
        this.status = 'error';
        this.errorMessage =
          error instanceof Error ? error.message : 'Failed to load QA summary.';
      } finally {
        if (this.requestId === requestId) {
          this.pendingPromise = null;
        }
      }
    })();

    this.pendingPromise = pending;
    await pending;
  }

  private resetState(): void {
    this.status = 'idle';
    this.summary = null;
    this.errorMessage = '';
    this.pendingPromise = null;
    this.resetDatasetState();
    this.resetSchemaState();
  }

  get isBlocked(): boolean {
    if (this.isDatasetView || this.isSchemaView) {
      return this.disabled;
    }
    return this.disabled || this.status !== 'ready';
  }
}
